import { View, ScrollView, Pressable, GestureResponderEvent } from 'react-native';
import { Link, usePathname } from 'expo-router';
import { Text } from '@/components/gluestack/text';
import { VStack } from '@/components/gluestack/vstack';
import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';
import {
  feedTypes,
  peopleListTypes,
  photoListTypes,
  eventListTypes,
  FeedType,
  PeopleListType,
  PhotoListType,
  EventListType,
} from '~/constants/lists';
import { getPeopleListHref } from '~/components/domain/members/peopleListItems';
import {
  triggerPageNavigationReset,
  triggerPageRefresh,
  clearStoredScrollPosition,
} from '~/hooks/useInfiniteListUtils';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import {
  EarthIcon,
  Location01Icon,
  Search02Icon,
  UserCircleIcon,
  EyeIcon,
  SparklesIcon,
  BirthdayCakeIcon,
  Clock01Icon,
  SquareLock01Icon,
  FavouriteIcon,
  StarIcon,
  Orbit01Icon,
  Calendar03Icon,
  UserGroupIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import {
  EarthIcon as EarthSolidIcon,
  Location01Icon as Location01SolidIcon,
  Search02Icon as Search02SolidIcon,
  UserCircleIcon as UserCircleSolidIcon,
  EyeIcon as EyeSolidIcon,
  SparklesIcon as SparklesSolidIcon,
  BirthdayCakeIcon as BirthdayCakeSolidIcon,
  Clock01Icon as Clock01SolidIcon,
  SquareLock01Icon as SquareLock01SolidIcon,
  FavouriteIcon as FavouriteSolidIcon,
  StarIcon as StarSolidIcon,
  Orbit01Icon as Orbit01SolidIcon,
  Calendar03Icon as Calendar03SolidIcon,
  UserGroupIcon as UserGroupSolidIcon,
} from '@hugeicons-pro/core-solid-rounded';
import { HugeiconsProps } from '@hugeicons/react-native';

// Icon mappings
const feedIcons: Record<FeedType, [HugeiconsProps['icon'], HugeiconsProps['icon']]> = {
  global: [EarthIcon, EarthSolidIcon],
  local: [Location01Icon, Location01SolidIcon],
  network: [StarIcon, StarSolidIcon],
  search: [Search02Icon, Search02SolidIcon],
};

const peopleIcons: Record<PeopleListType, [HugeiconsProps['icon'], HugeiconsProps['icon']]> = {
  online: [UserCircleIcon, UserCircleSolidIcon],
  nearby: [Location01Icon, Location01SolidIcon],
  following: [StarIcon, StarSolidIcon],
  followers: [Orbit01Icon, Orbit01SolidIcon],
  visitors: [EyeIcon, EyeSolidIcon],
  new: [SparklesIcon, SparklesSolidIcon],
  birthdays: [BirthdayCakeIcon, BirthdayCakeSolidIcon],
  search: [Search02Icon, Search02SolidIcon],
};

const photoIcons: Record<PhotoListType, [HugeiconsProps['icon'], HugeiconsProps['icon']]> = {
  latest: [Clock01Icon, Clock01SolidIcon],
  network: [StarIcon, StarSolidIcon],
  private: [SquareLock01Icon, SquareLock01SolidIcon],
  favorites: [FavouriteIcon, FavouriteSolidIcon],
};

const eventIcons: Record<EventListType, [HugeiconsProps['icon'], HugeiconsProps['icon']]> = {
  upcoming: [Calendar03Icon, Calendar03SolidIcon],
  nearby: [Location01Icon, Location01SolidIcon],
  network: [UserGroupIcon, UserGroupSolidIcon],
  past: [Clock01Icon, Clock01SolidIcon],
};

// NavigationItem component for individual navigation links
interface NavigationItemProps {
  href: string;
  label: string;
  isActive?: boolean;
  pageKey?: string;
  icons: [HugeiconsProps['icon'], HugeiconsProps['icon']];
}

const NavigationItem = ({
  href,
  label,
  isActive,
  pageKey,
  icons,
}: NavigationItemProps): React.ReactElement => {
  const handlePress = (event: GestureResponderEvent) => {
    if (!pageKey) return;

    if (isActive) {
      const nativeEvent = event.nativeEvent as GestureResponderEvent['nativeEvent'] & {
        altKey?: boolean;
        ctrlKey?: boolean;
        metaKey?: boolean;
        shiftKey?: boolean;
        button?: number;
      };
      const isModifiedClick =
        nativeEvent.metaKey ||
        nativeEvent.ctrlKey ||
        nativeEvent.shiftKey ||
        nativeEvent.altKey ||
        nativeEvent.button === 1;

      if (isModifiedClick) return;

      event.preventDefault();
    }

    clearStoredScrollPosition(pageKey);

    if (isActive) {
      triggerPageRefresh(pageKey);
      return;
    }

    // The destination may already be mounted in another tab family on large
    // screens, in which case it would otherwise resume its previous scroll.
    // No-op if the destination is not currently mounted.
    triggerPageNavigationReset(pageKey);
  };

  const [strokeIcon, solidIcon] = icons;

  return (
    <Link
      href={href as any}
      replace={isActive}
      asChild
      className={`${isActive ? '-my-1.5 -ml-6 rounded-r-xl bg-background-100 py-1.5 pl-6 dark:bg-background-100' : ''}`}>
      <Pressable onPress={handlePress} className="flex-row items-center gap-2">
        <HugeIconsWrapper
          icon={isActive ? solidIcon : strokeIcon}
          size={20}
          className={isActive ? 'text-typography-900' : 'text-typography-500'}
        />
        <Text
          size="sm"
          className={`text-typography-700 ${isActive ? 'pr-2 font-bold text-typography-900' : ''}`}>
          {label}
        </Text>
      </Pressable>
    </Link>
  );
};

// NavigationSection component for grouping navigation items
interface NavigationSectionProps {
  title: string;
  children: ReactNode;
}

const NavigationSection = ({ title, children }: NavigationSectionProps) => {
  return (
    <View>
      <Text size="sm" bold className="mb-2 mt-4 uppercase text-tertiary-500">{title}</Text>
      <VStack space="sm" className="pl-0">
        {children}
      </VStack>
    </View>
  );
};

export function LargeScreenLeftPanel() {
  const pathname = usePathname();
  const { t } = useTranslation();

  const isItemActive = (section: string, itemId: string) => {
    return pathname.includes(`/${section}/${itemId}`);
  };

  return (
    <View className="w-48 bg-background-0 xl:w-56">
      <ScrollView className="pb-10 pt-3">
        <VStack space="md" className="px-6 android:pb-8">
          {/* Feeds Section */}
          <NavigationSection title={t('navigation.feeds')}>
            {feedTypes.map((feed) => (
              <NavigationItem
                key={feed}
                href={`/feeds/${feed}`}
                label={t(`feeds.${feed}_short`)}
                isActive={isItemActive('feeds', feed)}
                pageKey={`/feeds/${feed}`}
                icons={feedIcons[feed]}
              />
            ))}
          </NavigationSection>

          {/* People Section */}
          <NavigationSection title={t('navigation.people')}>
            {peopleListTypes.map((type) => {
              const isActive = isItemActive('people', type);
              const href = getPeopleListHref(type);
              // When active, key off the current pathname so refresh works
              // on filtered subroutes (e.g. /people/nearby/recent) where the
              // screen registers a more specific pageKey than the menu href.
              const pageKey = isActive ? pathname : href;
              return (
                <NavigationItem
                  key={type}
                  href={href}
                  label={t(`people.${type}_short`)}
                  isActive={isActive}
                  pageKey={pageKey}
                  icons={peopleIcons[type]}
                />
              );
            })}
          </NavigationSection>

          {/* Photos Section */}
          <NavigationSection title={t('navigation.photos')}>
            {photoListTypes.map((type) => (
              <NavigationItem
                key={type}
                href={`/photos/${type}`}
                label={t(`photos.${type}_short`)}
                isActive={isItemActive('photos', type)}
                pageKey={`/photos/${type}`}
                icons={photoIcons[type]}
              />
            ))}
          </NavigationSection>

          {/* Events Section */}
          <NavigationSection title={t('navigation.events')}>
            {eventListTypes.map((type) => (
              <NavigationItem
                key={type}
                href={`/events/${type}`}
                label={t(`events.${type}_short`)}
                isActive={isItemActive('events', type)}
                pageKey={`/events/${type}`}
                icons={eventIcons[type]}
              />
            ))}
          </NavigationSection>
        </VStack>
      </ScrollView>
    </View>
  );
}
